/**
 * 自由オブジェクトの定義カタログ。クライアント・サーバー共通の固定表。
 * インスタンスは definitionId だけ持ち、未知 ID は 1m 直立箱に落とす。
 */

import type { ObjectDefinition, ObjectPart, Vec3 } from './types';
import {
  DEFAULT_LIGHT_COLOR,
  DEFAULT_LIGHT_INTENSITY,
  DEFAULT_LIGHT_RANGE,
} from './validation';

function part(min: Vec3, max: Vec3, material?: string): ObjectPart {
  return material ? { bounds: { min, max }, material } : { bounds: { min, max } };
}

/** 局所 +Y 上、+Z 正面。座面は床から 45cm。 */
const CHAIR_W = 0.5;
const CHAIR_D = 0.5;
const CHAIR_H = 1;
const CHAIR_HX = CHAIR_W / 2;
const CHAIR_HZ = CHAIR_D / 2;
const CHAIR_MIN_Y = -CHAIR_H / 2;
const CHAIR_MAX_Y = CHAIR_H / 2;
const CHAIR_SEAT_THICK = 0.07;
const CHAIR_SEAT_FROM_FLOOR = 0.45;
const CHAIR_SEAT_TOP = CHAIR_MIN_Y + CHAIR_SEAT_FROM_FLOOR;
const CHAIR_SEAT_BOTTOM = CHAIR_SEAT_TOP - CHAIR_SEAT_THICK;
const CHAIR_LEG = 0.05;
const CHAIR_LEG_INSET = 0.03;
const CHAIR_BACK_T = 0.06;

function chairLeg(sx: 1 | -1, sz: 1 | -1): ObjectPart {
  const cx = sx * (CHAIR_HX - CHAIR_LEG_INSET - CHAIR_LEG / 2);
  const cz = sz * (CHAIR_HZ - CHAIR_LEG_INSET - CHAIR_LEG / 2);
  return part(
    { x: cx - CHAIR_LEG / 2, y: CHAIR_MIN_Y, z: cz - CHAIR_LEG / 2 },
    { x: cx + CHAIR_LEG / 2, y: CHAIR_SEAT_BOTTOM, z: cz + CHAIR_LEG / 2 }
  );
}

const UPRIGHT_CHAIR: ObjectDefinition = {
  id: 'box-upright',
  placement: 'upright',
  bounds: {
    min: { x: -CHAIR_HX, y: CHAIR_MIN_Y, z: -CHAIR_HZ },
    max: { x: CHAIR_HX, y: CHAIR_MAX_Y, z: CHAIR_HZ },
  },
  material: 'laminated-wood',
  parts: [
    part(
      { x: -CHAIR_HX, y: CHAIR_SEAT_BOTTOM, z: -CHAIR_HZ },
      { x: CHAIR_HX, y: CHAIR_SEAT_TOP, z: CHAIR_HZ }
    ),
    part(
      { x: -CHAIR_HX, y: CHAIR_SEAT_TOP, z: -CHAIR_HZ },
      { x: CHAIR_HX, y: CHAIR_MAX_Y, z: -CHAIR_HZ + CHAIR_BACK_T }
    ),
    chairLeg(-1, -1),
    chairLeg(1, -1),
    chairLeg(-1, 1),
    chairLeg(1, 1),
  ],
};

const FIXTURE_BACK_T = 0.012;

const FIXTURE_BOX: ObjectDefinition = {
  id: 'box-fixture',
  placement: 'surfaceMount',
  bounds: {
    min: { x: -0.5, y: -0.1, z: -0.15 },
    max: { x: 0.5, y: 0, z: 0.15 },
  },
  material: 'light',
  parts: [
    part(
      { x: -0.5, y: -0.1, z: -0.15 },
      { x: 0.5, y: -FIXTURE_BACK_T, z: 0.15 }
    ),
    part({ x: -0.5, y: -FIXTURE_BACK_T, z: -0.15 }, { x: 0.5, y: 0, z: 0.15 }, 'black'),
  ],
  components: {
    lights: [
      {
        kind: 'point',
        color: DEFAULT_LIGHT_COLOR,
        intensity: DEFAULT_LIGHT_INTENSITY,
        range: DEFAULT_LIGHT_RANGE,
        offset: { x: 0, y: -0.05, z: 0 },
      },
    ],
  },
};

const FALLBACK_BOX: ObjectDefinition = {
  id: 'box-fallback',
  placement: 'upright',
  bounds: {
    min: { x: -0.5, y: -0.5, z: -0.5 },
    max: { x: 0.5, y: 0.5, z: 0.5 },
  },
  material: 'default',
};

export const OBJECT_DEFINITIONS: readonly ObjectDefinition[] = [UPRIGHT_CHAIR, FIXTURE_BOX];

const BY_ID = new Map(OBJECT_DEFINITIONS.map((definition) => [definition.id, definition]));

export function getObjectDefinition(id: string): ObjectDefinition | undefined {
  return BY_ID.get(id);
}

/** 未知 ID は 1m 直立箱。表示・設置が止まらないようにする。 */
export function resolveObjectDefinition(id: string): ObjectDefinition {
  return BY_ID.get(id) ?? { ...FALLBACK_BOX, id };
}

export function isKnownObjectDefinitionId(id: string): boolean {
  return BY_ID.has(id);
}
